import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface TenantUrlHelpers {
  /** Current tenant slug from URL params */
  tenantSlug: string | undefined;
  /** Current tenant slug from auth context (selected tenant) */
  currentTenantSlug: string | undefined;
  /** Prefix a path with the current tenant slug */
  tenantPath: (path: string) => string;
  /** Navigate to a tenant-prefixed path */
  tenantNavigate: (path: string, options?: { replace?: boolean }) => void;
  /** Build a full tenant URL for a different tenant */
  buildTenantUrl: (slug: string, path: string) => string;
}

/**
 * Hook for building and navigating to tenant-aware URLs
 * 
 * Usage:
 * ```tsx
 * const { tenantPath, tenantNavigate } = useTenantUrl();
 * 
 * // Build a path
 * const ordersUrl = tenantPath('/orders'); // -> /t/trooper-ch/orders
 * 
 * // Navigate
 * tenantNavigate('/orders/123');
 * ```
 */
export const useTenantUrl = (): TenantUrlHelpers => {
  const navigate = useNavigate();
  const { tenantSlug: urlTenantSlug } = useParams<{ tenantSlug: string }>();
  const { currentTenant } = useAuth();

  // Get the current tenant slug - prefer URL param, fall back to auth context
  const currentTenantSlug = currentTenant?.shopify_shop_name;
  const effectiveSlug = urlTenantSlug || currentTenantSlug;

  /**
   * Prefix a path with the current tenant slug
   * @param path - The path to prefix (e.g., '/orders' or '/orders/123')
   * @returns The tenant-prefixed path (e.g., '/t/trooper-ch/orders')
   */
  const tenantPath = useCallback((path: string): string => {
    if (!effectiveSlug) {
      console.warn('No tenant slug available for tenantPath()');
      return path;
    }
    
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Don't double-prefix if already has tenant
    if (normalizedPath.startsWith('/t/')) {
      return normalizedPath;
    }
    
    return `/t/${effectiveSlug}${normalizedPath}`;
  }, [effectiveSlug]);

  /**
   * Navigate to a tenant-prefixed path
   * @param path - The path to navigate to (will be prefixed with tenant)
   * @param options - Navigation options
   */
  const tenantNavigate = useCallback((path: string, options?: { replace?: boolean }) => {
    const fullPath = tenantPath(path);
    navigate(fullPath, options);
  }, [navigate, tenantPath]);

  /**
   * Build a full tenant URL for a specific tenant
   * @param slug - The tenant slug
   * @param path - The path within that tenant
   */
  const buildTenantUrl = useCallback((slug: string, path: string): string => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `/t/${slug}${normalizedPath}`;
  }, []);

  return {
    tenantSlug: urlTenantSlug,
    currentTenantSlug,
    tenantPath,
    tenantNavigate,
    buildTenantUrl,
  };
};

export default useTenantUrl;


